<template>
    <div class="checkout-form-container">
        <!-- Contact Information -->
        <div class="checkout-section">
            <h3 class="checkout-section-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-3">Contact Information</h3>
            <div class="checkout-form-fields">
                <div class="form-group mb-4">
                    <label class="text-lg text-[var(--charcoal-dark)] font-medium mb-1" for="name">Full Name *</label>
                    <div class="input-wrapper">
                        <input 
                            type="text" 
                            id="name" 
                            v-model="nameField.value.value"
                            @blur="nameField.handleBlur"
                            @input="e => nameField.handleInput(e.target.value)"
                            :class="[
                                'form-input text-base text-[var(--charcoal-dark)] border rounded-full h-9 px-3 py-1 bg-white w-full outline-0 pr-10',
                                nameField.hasError.value ? 'border-red-600 border-2' : 'border-[var(--bone)] focus:border-[var(--primary)]'
                            ]"
                            v-bind="nameField.ariaAttributes.value"
                            placeholder="Your Name" 
                            required 
                        />
                        <svg
                            v-if="nameField.hasError.value"
                            class="error-icon-input"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <FieldError :message="nameField.errorMessage.value" error-id="name-error" />
                </div>
                <div class="form-group mb-4">
                    <label class="text-lg text-[var(--charcoal-dark)] font-medium mb-1" for="email">Email Address *</label>
                    <div class="input-wrapper">
                        <input 
                            type="email" 
                            id="email" 
                            v-model="emailField.value.value"
                            @blur="emailField.handleBlur"
                            @input="e => emailField.handleInput(e.target.value)"
                            :class="[
                                'form-input text-base text-[var(--charcoal-dark)] border rounded-full h-9 px-3 py-1 bg-white w-full outline-0 pr-10',
                                emailField.hasError.value ? 'border-red-600 border-2' : 'border-[var(--bone)] focus:border-[var(--primary)]'
                            ]"
                            v-bind="emailField.ariaAttributes.value"
                            placeholder="your@email.com" 
                            required 
                        />
                        <svg
                            v-if="emailField.hasError.value"
                            class="error-icon-input"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <FieldError :message="emailField.errorMessage.value" error-id="email-error" />
                </div>
                <div class="form-group mb-4">
                    <label class="text-lg text-[var(--charcoal-dark)] font-medium mb-1" for="phone">Phone Number *</label>
                    <div :class="['phone-wrapper', phoneField.hasError.value ? 'has-error' : '']">
                        <FormPhone 
                            label="" 
                            v-model="form.phone_number" 
                            required 
                            :phone-code="form.phone_code" 
                            @update="handlePhoneUpdate" 
                        />
                    </div>
                    <FieldError :message="phoneField.errorMessage.value" error-id="phone-error" />
                </div>
                <div class="form-group mb-4">
                    <label class="text-lg text-[var(--charcoal-dark)] font-medium mb-1" for="comment">Comments (Optional)</label>
                    <textarea id="comment" v-model="form.comment" class="form-textarea text-base text-[var(--charcoal-dark)] border border-[var(--bone)] focus:border-[var(--primary)] rounded-2xl p-3 bg-white w-full h-36 outline-0 resize-none" rows="4" placeholder="Any special requests or comments..."></textarea>
                </div>
            </div>
        </div>

        <!-- Payment Information -->
        <div class="checkout-section bg-[#F5F1EC] shadow-[0px_4.07px_6.11px_0px_#0000001A] border border-[var(--bone)] rounded-xl mb-5">
            <h3 class="checkout-section-title px-4 py-3 text-lg lg:text-[22px] text-[var(--charcoal)] font-bold border-b border-[var(--bone)]">Payment Information</h3>
            <div class="payment-element-wrapper">
                <div id="payment-element" class="p-4"></div>
            </div>
            <ErrorList :errors="displayErrors" />
        </div>

        <!-- Terms and Conditions -->
        <div class="checkout-section">
            <div class="checkbox-group flex flex-col">
                <label class="checkbox-label text-lg text-[var(--charcoal-dark)] font-medium mb-2 flex items-center">
                    <input class="w-5 h-5 mr-2" type="checkbox" v-model="form.cancellations_policy" required />
                    <span>I have read and accept all <a href="#" @click.prevent="openPolicy" class="policy-link underline text-[var(--primary)]">cancellations terms</a> *</span>
                </label>
                <!-- Dynamic Custom Checkout Terms -->
                <label 
                    v-for="term in activeCustomTerms" 
                    :key="term.key"
                    class="checkbox-label text-lg text-[var(--charcoal-dark)] font-medium mb-2 flex items-start"
                >
                    <input 
                        class="w-5 h-5 mr-2 mt-1 flex-shrink-0" 
                        type="checkbox" 
                        v-model="form.customTermAcknowledgments[term.key]"
                        :required="term.required"
                    />
                    <span>{{ term.label }}{{ term.required ? ' *' : '' }}</span>
                </label>
                <label class="checkbox-label text-lg text-[var(--charcoal-dark)] font-medium mb-2 flex items-center">
                    <input class="w-5 h-5 mr-2" type="checkbox" v-model="form.getemailupdates" />
                    <span>Get future email updates from {{ company_name }}</span>
                </label>
            </div>
        </div>

        <!-- Submit Button -->
        <div class="checkout-section mt-3">
            <button class="btn-checkout btn-primary" @click="handleSubmit" :disabled="isProcessing" :class="{ 'opacity-60 cursor-not-allowed': isProcessing }">{{ isProcessing ? 'Processing...' : 'Complete Booking' }}</button>
            <PolicyFooter />
        </div>
    </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { watch } from 'vue';
import FormInput from '../User/FormInput.vue';
import FormPhone from '../User/FormPhone.vue';
import FormTextarea from '../User/FormTextarea.vue';
import FormCheckbox from '../User/FormCheckbox.vue';
import PaymentHeader from '../Layout/PaymentHeader.vue';
import ErrorList from '../Error/ErrorList.vue';
import PolicyFooter from '../Layout/PolicyFooter.vue';
import FieldError from '../Forms/FieldError.vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getPolicyByTenant } from '../../utils/policyUtils';
import { CountryCodes } from '../../utils/geoUtils';
import { useFieldValidation } from '../../composables/useFieldValidation';
import { nameRules, emailRules, phoneRules } from '../../utils/validationRules';

export default {
    name: "CheckoutForm",
    components: { FormInput, FormPhone, FormTextarea, FormCheckbox, PaymentHeader, ErrorList, PolicyFooter, FieldError },
    props: ["items", "tenantId", "errors", "globalTotal"],
    setup() {
        // Initialize field validation composables in setup
        const nameField = useFieldValidation({
            fieldName: 'name',
            rules: nameRules,
            initialValue: ''
        });
        
        const emailField = useFieldValidation({
            fieldName: 'email',
            rules: emailRules,
            initialValue: ''
        });
        
        const phoneField = useFieldValidation({
            fieldName: 'phone',
            rules: phoneRules,
            initialValue: ''
        });
        
        return {
            nameField,
            emailField,
            phoneField
        };
    },
    data() {
        return {
            form: {
                name: '',
                phone_number: '',
                phone_code: '+1',
                email: '',
                getemailupdates: false,
                cancellations_policy: false,
                comment: '',
                paymentIntentId: null,
                stripeCustomerId: null,
                customTermAcknowledgments: {}
            },
            company_name: '',
            comboIds: 0,
            cartItemLength: 0,
            receipt_email: null,
            stripe: null,
            elements: null,
            localErrors: [],
            loaderInstance: null,
            isProcessing: false,
            cooldownTimer: null
        };
    },
    async created() {
        this.company_name = this.$store.state.tourPackage?.tourPackageData[0]?.company_name;
        this.comboIds = this.$store.state.comboIds;
        
        // Initialize custom term acknowledgments
        this.initializeCustomTermAcknowledgments();
        
        // Set up watchers to sync composable values back to form object
        watch(() => this.nameField.value.value, (newVal) => {
            this.form.name = newVal;
        });
        
        watch(() => this.emailField.value.value, (newVal) => {
            this.form.email = newVal;
        });
        
        watch(() => this.phoneField.value.value, (newVal) => {
            this.form.phone_number = newVal;
        });
    },
    async mounted() {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        this.initializePayment();
    },
    beforeUnmount() {
        // Clean up cooldown timer if component is destroyed
        if (this.cooldownTimer) {
            clearTimeout(this.cooldownTimer);
            this.cooldownTimer = null;
        }
    },
    computed: {
        itemTotal() {
            return Object.values(this.items).map(item => item.total);
        },
        displayErrors() {
            const parentErrors = this.errors || [];
            return [...parentErrors, ...this.localErrors];
        },
        customCheckoutTerms() {
            return this.$store.state.customCheckoutTerms || [];
        },
        activeCustomTerms() {
            return this.customCheckoutTerms.filter(term => term.status === 'active');
        }
    },
    watch: {
        itemTotal: {
            handler(newValues, oldValues) {
                if (oldValues && JSON.stringify(newValues) !== JSON.stringify(oldValues)) {
                    this.initializePayment();
                }
            },
            deep: true
        }
    },
    methods: {
        async initializePayment() {
            if (this.itemTotal.length === 0) {
                this.$router.push('/initialize');
                return;
            }

            this.showLoader();
            try {
                const { clientSecret, paymentIntentId } = await this.createPaymentIntent();
                this.form.paymentIntentId = paymentIntentId;
                this.setupStripeElements(clientSecret);
            } catch (error) {
                this.handleError('Payment initialization failed', error);
            } finally {
                this.hideLoader();
            }
        },

        async createPaymentIntent() {
            // Let errors propagate to initializePayment()'s catch instead of swallowing them
            // here - catching and calling this.handleError(error) with one argument doesn't
            // match handleError(message, error)'s signature and throws reading .message off
            // an undefined second argument, masking the real backend error behind a crash.
            const response = await axios.post('/create-payment-intent', {
                items: this.items
            });

            const data = response.data;
            if (data.error) throw new Error(data.error.message);
            return data;
        },

        setupStripeElements(clientSecret) {
            const appearance = {
                theme: 'stripe', // Change to 'stripe', 'night', 'flat', or 'none'
            };

            const options = {
                layout: { type: 'auto' }, // Change to 'accordion', 'tabs', 'inline', or 'auto'
                paymentMethodOrder: ['apple_pay', 'google_pay', 'card'], // Prioritize payment methods
                business: { name: 'Native American Tours' },
                wallets: { applePay: 'auto', googlePay: 'auto' },
                defaultValues: { billingDetails: { address: { country: this.$store.state.countryCode } } }
            };

            this.elements = this.stripe.elements({ clientSecret, appearance });
            const paymentElement = this.elements.create('payment', options);
            paymentElement.mount('#payment-element');
        },

        async handleSubmit() {
            if (this.isProcessing) return;
            this.isProcessing = true;
            this.showLoader();
            this.localErrors = [];

            // Clear any existing cooldown timer
            if (this.cooldownTimer) {
                clearTimeout(this.cooldownTimer);
                this.cooldownTimer = null;
            }

            try {
                // 1) Validate inline fields first
                const nameValid = this.nameField.validate();
                const emailValid = this.emailField.validate();
                const phoneValid = this.phoneField.validate();

                if (!nameValid || !emailValid || !phoneValid) {
                    // Mark all fields as touched to show errors
                    this.nameField.isTouched.value = true;
                    this.emailField.isTouched.value = true;
                    this.phoneField.isTouched.value = true;

                    // Scroll to first invalid field
                    this.$nextTick(() => {
                        const firstInvalidField = document.querySelector('[aria-invalid="true"]');
                        if (firstInvalidField) {
                            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    });

                    return; // Prevent submission
                }

                // 2) Validate Stripe payment element before proceeding
                if (this.elements) {
                    const { error: submitError } = await this.elements.submit();
                    if (submitError) {
                        // Payment information is incomplete or invalid
                        this.localErrors.push(submitError.message || 'Please complete your payment information.');

                        // Scroll to payment section
                        this.$nextTick(() => {
                            const paymentElement = document.querySelector('#payment-element');
                            if (paymentElement) {
                                paymentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        });

                        return; // Prevent submission
                    }
                }

                // 3) Validate up front (includes combo rule now)
                if (!this.validateForm()) {
                    return; // finally{} will hide the loader
                }

                // 4) Check availability
                const isAvailable = await this.checkAvailability();
                if (!isAvailable) return;

                // 5) Build payload safely
                const phone_number = CountryCodes.formatPhoneNumber(
                    this.form.phone_code,
                    this.form.phone_number
                );

                const payload = {
                    items: this.items,
                    combo_ids: this.comboIds,
                    name: this.form.name?.trim(),
                    email: this.form.email?.trim(),
                    phone_number,
                    comment: this.form.comment ?? '',
                    getemailupdates: !!this.form.getemailupdates,
                    cancellations_policy: !!this.form.cancellations_policy,
                    payment_intent_id: this.form.paymentIntentId ?? null,
                    stripe_customer_id: this.form.stripeCustomerId ?? null,
                    custom_term_acknowledgments: this.form.customTermAcknowledgments,
                    ...this.globalTotal
                };

                // Persist full customer data before any redirect (e.g. 3DS) so
                // PaymentSuccess.vue can reconstruct the booking if the browser
                // navigates away during confirmPayment.
                this.$store.dispatch('storeCustomer', payload);

                // 6) Persist pre-booking BEFORE verification to avoid timing issues
                await axios.post('/create-pre-booking', payload);

                // 7) If we already have a PI, try verifying it first
                const verifyPaymentIntent = await axios.post('/verify-payment', {
                    paymentIntentId: this.form.paymentIntentId
                });

                if (verifyPaymentIntent?.data?.status === 'succeeded') {
                    // Payment already completed (e.g., after 3DS redirect).
                    // Go directly to booking creation via parent instead of
                    // confirming payment again, which could cause a duplicate.
                    await axios.post('/delete-pre-booking', payload);
                    this.hideLoader();
                    this.$emit('onsubmit', this.form);
                    return;
                }

                // 6) Confirm payment (Stripe or similar)
                const { paymentIntent, error } = await this.confirmPayment();
                if (error) throw new Error(error.message);

                // 7) Store PI ID and emit
                this.form.paymentIntentId = paymentIntent.id;
                this.$emit('onsubmit', this.form);
            } catch (error) {
                this.handleError('Payment processing failed', error);
            } finally {
                this.hideLoader();
                // Add a 5-second cooldown before allowing resubmission
                this.cooldownTimer = setTimeout(() => {
                    this.isProcessing = false;
                    this.cooldownTimer = null;
                }, 5000);
            }
        },

        validateForm() {
            const cartItemLength = Object.keys(this.items || {}).length;

            // Note: Name, email, and phone are now validated by inline validation
            // This method only validates checkboxes and business rules
            const validations = [
                [!this.form?.cancellations_policy, 'Please accept terms and conditions']
            ];

            // Validate required custom checkout terms
            this.activeCustomTerms.forEach(term => {
                if (term.required && !this.form.customTermAcknowledgments[term.key]) {
                    validations.push([true, `Please acknowledge: "${term.label}"`]);
                }
            });

            // Business rule: if combo checkout is in play, must have at least 2 packages
            if (this.comboIds.length && cartItemLength === 1) {
                validations.push([true, 'To proceed, please ensure you have selected at least two packages.']);
            }

            this.localErrors = validations
                .filter(([condition]) => condition)
                .map(([, message]) => message);

            return this.localErrors.length === 0;
        },

        async checkAvailability() {
            try {
                if (this.receipt_email && this.receipt_email !== this.form.email) {
                    Swal.fire({
                        toast: true,
                        title: 'Error',
                        html: 'Email address can\'t be changed after payment is set up. Please refresh the page to update your details.',
                        icon: 'error'
                    });

                    return false;
                }

                this.receipt_email = this.form.email;

                const cartItem = this.$store.state.cartItem;

                const payload = {
                    name: this.form.name,
                    email: this.form.email,
                    phone_number: this.form.phone_number,
                    payment_intent_id: this.form.paymentIntentId
                };

                this.$store.dispatch('storeCustomer', payload);

                // Handle prepare-to-save-card API call with error handling
                try {
                    const saveCard = await axios.post('/prepare-to-save-card-on-payment-intent', payload);
                    this.form.stripeCustomerId = saveCard.data.stripeCustomerId;
                } catch (error) {
                    // Extract error message from backend response
                    const errorMessage = error.response?.data?.error 
                        || error.response?.data?.message 
                        || 'We could not process your payment at the moment. Please refresh the page and try again.';

                    this.localErrors.push(errorMessage);
                    return false;
                }

                const response = await axios.post('/bulk-check-available-seats', { items: cartItem });

                const errors = Object.entries(response.data)
                    .filter(([, value]) => value.success === 'false')
                    .map(([, value]) => value.message);

                if (errors.length) {
                    this.handleAvailabilityError(errors);
                    return false;
                }
                return true;
            } catch (error) {
                // Handle any other unexpected errors
                console.error('Availability check error:', error);
                const errorMessage = error.response?.data?.error 
                    || error.response?.data?.message 
                    || error.message 
                    || 'An error occurred while checking availability.';
                this.localErrors.push(errorMessage);
                return false;
            }
        },

        async confirmPayment() {
            return this.stripe.confirmPayment({
                elements: this.elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success`,
                    payment_method_data: {
                        billing_details: {
                            name: this.form.name,
                            email: this.form.email,
                            phone: this.form.phone_number
                        }
                    }
                },
                redirect: 'if_required'
            });
        },

        handlePhoneUpdate({ phone_num, phone_ext }) {
            try {
                this.form.phone_number = phone_num;
                this.form.phone_code = phone_ext;
                this.phoneField.handleInput(phone_num);
            } catch (error) {
                console.error('Phone update error:', error);
                // Fall back to validating current form value
                this.phoneField.handleInput(this.form.phone_number);
            }
        },

        openPolicy() {
            Swal.fire({
                toast: true,
                title: 'Terms and conditions',
                html: getPolicyByTenant(this.tenantId),
                width: '80vw'
            });
        },

        initializeCustomTermAcknowledgments() {
            // Initialize all custom terms to false
            const acknowledgments = {};
            this.activeCustomTerms.forEach(term => {
                acknowledgments[term.key] = false;
            });
            this.form.customTermAcknowledgments = acknowledgments;
        },

        showLoader() {
            this.loaderInstance = this.$loading.show();
        },

        hideLoader() {
            this.loaderInstance?.hide();
            this.loaderInstance = null;
        },

        handleError(message, error) {
            console.error(message, error);
            // Prefer the backend's actual message over error.message, which for a failed HTTP
            // request is just Axios's generic "Request failed with status code 422" and tells
            // the customer nothing useful.
            const backendMessage = error?.response?.data?.message;
            this.localErrors.push(backendMessage || error?.message || message);
        },

        handleAvailabilityError(errors) {
            Swal.fire({
                toast: true,
                title: 'Errors!',
                html: errors[0],
                icon: 'error'
            });
            this.$emit('updateSeatErrors', errors);
        }
    }
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
}

.error-icon-input {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #DC2626;
  pointer-events: none;
}

.phone-wrapper {
  position: relative;
}

.phone-wrapper.has-error :deep(.phone-wrap) {
  border: 2px solid #DC2626;
  border-radius: 9999px;
}
</style>
